/**
 * Logix IndexedDB Wrapper
 * Handles offline storage for shift log entries, checklist log entries, photos, and sync queue.
 * DB: logix_offline v1, 6 stores.
 */

const DB_NAME = "logix_offline"
const DB_VERSION = 1

export const STORES = {
  SHIFT_LOG_ENTRIES: "shift_log_entries",
  CHECKLIST_LOG_ENTRIES: "checklist_log_entries",
  PHOTOS: "photos",
  PENDING_SYNC: "pending_sync",
  CACHE: "cache",
  META: "meta"
}

let dbInstance = null

export async function openDB() {
  if (dbInstance) return dbInstance

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onerror = () => reject(request.error)

    request.onsuccess = () => {
      dbInstance = request.result
      dbInstance.onclose = () => { dbInstance = null }
      resolve(dbInstance)
    }

    request.onupgradeneeded = (event) => {
      const db = event.target.result
      createStores(db)
    }
  })
}

function createStores(db) {
  // Shift log entries
  if (!db.objectStoreNames.contains(STORES.SHIFT_LOG_ENTRIES)) {
    const store = db.createObjectStore(STORES.SHIFT_LOG_ENTRIES, { keyPath: "client_uuid" })
    store.createIndex("shift_id", "shift_id", { unique: false })
    store.createIndex("synced", "synced", { unique: false })
    store.createIndex("created_at", "created_at", { unique: false })
  }

  // Checklist log entries
  if (!db.objectStoreNames.contains(STORES.CHECKLIST_LOG_ENTRIES)) {
    const store = db.createObjectStore(STORES.CHECKLIST_LOG_ENTRIES, { keyPath: "client_uuid" })
    store.createIndex("checklist_entry_id", "checklist_entry_id", { unique: false })
    store.createIndex("synced", "synced", { unique: false })
    store.createIndex("created_at", "created_at", { unique: false })
  }

  // Photos
  if (!db.objectStoreNames.contains(STORES.PHOTOS)) {
    const store = db.createObjectStore(STORES.PHOTOS, { keyPath: "client_uuid" })
    store.createIndex("parent_uuid", "parent_uuid", { unique: false })
    store.createIndex("synced", "synced", { unique: false })
  }

  // Pending sync queue
  if (!db.objectStoreNames.contains(STORES.PENDING_SYNC)) {
    const store = db.createObjectStore(STORES.PENDING_SYNC, { keyPath: "id", autoIncrement: true })
    store.createIndex("type", "type", { unique: false })
    store.createIndex("status", "status", { unique: false })
    store.createIndex("created_at", "created_at", { unique: false })
  }

  // General cache (checklist data, branding, etc.)
  if (!db.objectStoreNames.contains(STORES.CACHE)) {
    db.createObjectStore(STORES.CACHE, { keyPath: "key" })
  }

  // Meta (JWT token, last sync time, etc.)
  if (!db.objectStoreNames.contains(STORES.META)) {
    db.createObjectStore(STORES.META, { keyPath: "key" })
  }
}

async function withStore(storeName, mode, callback) {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, mode)
    const store = tx.objectStore(storeName)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
    callback(store, resolve, reject)
  })
}

// ===== SHIFT LOG ENTRIES =====

export async function saveShiftLogEntry(entry) {
  const data = {
    client_uuid: entry.client_uuid || generateUUID(),
    shift_id: entry.shift_id,
    snapshot_item_id: entry.snapshot_item_id,
    entry_type: entry.entry_type,
    occurrence_number: entry.occurrence_number,
    reading_value: entry.reading_value,
    reading_unit: entry.reading_unit,
    check_value: entry.check_value,
    action_completed: entry.action_completed,
    observation_text: entry.observation_text,
    remarks: entry.remarks,
    latitude: entry.latitude,
    longitude: entry.longitude,
    submitted_at: entry.submitted_at || new Date().toISOString(),
    synced: 0,
    created_at: new Date().toISOString()
  }

  await withStore(STORES.SHIFT_LOG_ENTRIES, "readwrite", (store) => {
    store.put(data)
  })

  await addToPendingSync("shift_log_entry", data.client_uuid, data)
  return data.client_uuid
}

export async function getUnsyncedShiftLogEntries() {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.SHIFT_LOG_ENTRIES, "readonly")
    const store = tx.objectStore(STORES.SHIFT_LOG_ENTRIES)
    const index = store.index("synced")
    const request = index.getAll(IDBKeyRange.only(0))
    request.onsuccess = () => resolve(request.result || [])
    request.onerror = () => reject(request.error)
  })
}

export async function markShiftLogEntrySynced(clientUuid) {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.SHIFT_LOG_ENTRIES, "readwrite")
    const store = tx.objectStore(STORES.SHIFT_LOG_ENTRIES)
    const request = store.get(clientUuid)
    request.onsuccess = () => {
      const entry = request.result
      if (entry) {
        entry.synced = 1
        entry.synced_at = new Date().toISOString()
        store.put(entry)
      }
      resolve()
    }
    request.onerror = () => reject(request.error)
  })
}

// ===== CHECKLIST LOG ENTRIES =====

export async function saveChecklistLogEntry(entry) {
  const data = {
    client_uuid: entry.client_uuid || generateUUID(),
    checklist_entry_id: entry.checklist_entry_id,
    plant_id: entry.plant_id,
    entry_type: entry.entry_type,
    occurrence_number: entry.occurrence_number,
    reading_value: entry.reading_value,
    reading_unit: entry.reading_unit,
    check_value: entry.check_value,
    action_completed: entry.action_completed,
    observation_text: entry.observation_text,
    remarks: entry.remarks,
    latitude: entry.latitude,
    longitude: entry.longitude,
    device_id: entry.device_id,
    submitted_at: entry.submitted_at || new Date().toISOString(),
    synced: 0,
    created_at: new Date().toISOString()
  }

  await withStore(STORES.CHECKLIST_LOG_ENTRIES, "readwrite", (store) => {
    store.put(data)
  })

  await addToPendingSync("checklist_log_entry", data.client_uuid, data)
  return data.client_uuid
}

export async function getUnsyncedChecklistLogEntries() {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.CHECKLIST_LOG_ENTRIES, "readonly")
    const store = tx.objectStore(STORES.CHECKLIST_LOG_ENTRIES)
    const index = store.index("synced")
    const request = index.getAll(IDBKeyRange.only(0))
    request.onsuccess = () => resolve(request.result || [])
    request.onerror = () => reject(request.error)
  })
}

export async function markChecklistLogEntrySynced(clientUuid) {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.CHECKLIST_LOG_ENTRIES, "readwrite")
    const store = tx.objectStore(STORES.CHECKLIST_LOG_ENTRIES)
    const request = store.get(clientUuid)
    request.onsuccess = () => {
      const entry = request.result
      if (entry) {
        entry.synced = 1
        entry.synced_at = new Date().toISOString()
        store.put(entry)
      }
      resolve()
    }
    request.onerror = () => reject(request.error)
  })
}

// ===== PHOTOS =====

export async function savePhoto(photo) {
  const data = {
    client_uuid: photo.client_uuid || generateUUID(),
    parent_uuid: photo.parent_uuid,
    parent_type: photo.parent_type,
    blob: photo.blob,
    filename: photo.filename || `photo_${Date.now()}.jpg`,
    content_type: photo.content_type || "image/jpeg",
    photo_type: photo.photo_type || "single",
    position: photo.position || 0,
    synced: 0,
    created_at: new Date().toISOString()
  }

  await withStore(STORES.PHOTOS, "readwrite", (store) => {
    store.put(data)
  })

  await addToPendingSync("photo", data.client_uuid, {
    client_uuid: data.client_uuid,
    parent_uuid: data.parent_uuid,
    parent_type: data.parent_type,
    filename: data.filename,
    content_type: data.content_type,
    photo_type: data.photo_type,
    position: data.position
  })

  return data.client_uuid
}

export async function getUnsyncedPhotos() {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.PHOTOS, "readonly")
    const store = tx.objectStore(STORES.PHOTOS)
    const index = store.index("synced")
    const request = index.getAll(IDBKeyRange.only(0))
    request.onsuccess = () => resolve(request.result || [])
    request.onerror = () => reject(request.error)
  })
}

export async function markPhotoSynced(clientUuid) {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.PHOTOS, "readwrite")
    const store = tx.objectStore(STORES.PHOTOS)
    const request = store.get(clientUuid)
    request.onsuccess = () => {
      const photo = request.result
      if (photo) {
        photo.synced = 1
        photo.synced_at = new Date().toISOString()
        store.put(photo)
      }
      resolve()
    }
    request.onerror = () => reject(request.error)
  })
}

// ===== PENDING SYNC QUEUE =====

export async function addToPendingSync(type, clientUuid, payload) {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.PENDING_SYNC, "readwrite")
    const store = tx.objectStore(STORES.PENDING_SYNC)
    const request = store.add({
      type,
      client_uuid: clientUuid,
      payload,
      status: "pending",
      attempts: 0,
      last_error: null,
      created_at: new Date().toISOString()
    })
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

export async function getPendingSync(type = null) {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.PENDING_SYNC, "readonly")
    const store = tx.objectStore(STORES.PENDING_SYNC)
    let request
    if (type) {
      const index = store.index("type")
      request = index.getAll(IDBKeyRange.only(type))
    } else {
      const index = store.index("status")
      request = index.getAll(IDBKeyRange.only("pending"))
    }
    request.onsuccess = () => resolve(request.result || [])
    request.onerror = () => reject(request.error)
  })
}

export async function updateSyncStatus(id, status, error = null) {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.PENDING_SYNC, "readwrite")
    const store = tx.objectStore(STORES.PENDING_SYNC)
    const request = store.get(id)
    request.onsuccess = () => {
      const item = request.result
      if (item) {
        item.status = status
        item.last_error = error
        if (status === "failed") item.attempts = (item.attempts || 0) + 1
        if (status === "synced") item.synced_at = new Date().toISOString()
        store.put(item)
      }
      resolve()
    }
    request.onerror = () => reject(request.error)
  })
}

export async function clearSyncedItems() {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.PENDING_SYNC, "readwrite")
    const store = tx.objectStore(STORES.PENDING_SYNC)
    const index = store.index("status")
    const request = index.openCursor(IDBKeyRange.only("synced"))
    let count = 0
    request.onsuccess = (event) => {
      const cursor = event.target.result
      if (cursor) {
        cursor.delete()
        count++
        cursor.continue()
      } else {
        resolve(count)
      }
    }
    request.onerror = () => reject(request.error)
  })
}

export async function getSyncStatusSummary() {
  const db = await openDB()
  const countByStatus = (status) => {
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.PENDING_SYNC, "readonly")
      const store = tx.objectStore(STORES.PENDING_SYNC)
      const index = store.index("status")
      const request = index.count(IDBKeyRange.only(status))
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }
  const [pending, syncing, failed] = await Promise.all([
    countByStatus("pending"),
    countByStatus("syncing"),
    countByStatus("failed")
  ])
  return { pending, syncing, failed, total: pending + syncing + failed }
}

// ===== CACHE STORE =====

export async function cacheSet(key, value) {
  await withStore(STORES.CACHE, "readwrite", (store) => {
    store.put({ key, value, updated_at: new Date().toISOString() })
  })
}

export async function cacheGet(key) {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.CACHE, "readonly")
    const store = tx.objectStore(STORES.CACHE)
    const request = store.get(key)
    request.onsuccess = () => resolve(request.result?.value ?? null)
    request.onerror = () => reject(request.error)
  })
}

// ===== META STORE =====

export async function metaSet(key, value) {
  await withStore(STORES.META, "readwrite", (store) => {
    store.put({ key, value })
  })
}

export async function metaGet(key) {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.META, "readonly")
    const store = tx.objectStore(STORES.META)
    const request = store.get(key)
    request.onsuccess = () => resolve(request.result?.value ?? null)
    request.onerror = () => reject(request.error)
  })
}

// ===== UTILITIES =====

export function generateUUID() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID()
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0
    const v = c === "x" ? r : (r & 0x3 | 0x8)
    return v.toString(16)
  })
}

export function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result.split(",")[1])
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

export function isIndexedDBAvailable() {
  try {
    return "indexedDB" in window && window.indexedDB !== null
  } catch {
    return false
  }
}

export async function clearAllData() {
  const db = await openDB()
  const storeNames = Object.values(STORES)
  for (const storeName of storeNames) {
    await new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, "readwrite")
      const store = tx.objectStore(storeName)
      const request = store.clear()
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }
}
